import axios from 'axios';
import type { BrokerageConnection, AlpacaAccountSummary } from './brokerageService';

export type StrategySpec = {
  key: string;
  name: string;
  version: number;
  mode: string;
  summary: string;
  rules: string[];
  confirmPhrase: string;
  tradeableTiers: string[];
  quant?: {
    onSaleAtrMult: number;
    overreactionAtrMult: number;
    capitulationAtr14Mult: number;
    tierFillFraction: { overreaction: number; capitulation: number };
    volTargetAtrPct: number;
    notes: string[];
  };
  defaults: {
    maxPositionPct: number;
    maxNotionalPerOrder: number;
    maxOpenPositions: number;
    dailyLossHaltPct: number;
    maxDrawdownPct?: number;
    maxDailyDeployPct?: number;
  };
};

export type StrategyDeployment = {
  id: number;
  strategyKey: string;
  status: string;
  killSwitchArmed: boolean;
  accountHint: string | null;
  maxPositionPct: number;
  maxNotionalPerOrder: number;
  maxOpenPositions: number;
  dailyLossHaltPct: number;
  maxDailyDeployPct?: number;
  /** Peak-to-trough equity drawdown that auto-pauses the sleeve (default 15). */
  maxDrawdownPct?: number;
  peakEquity?: number | null;
  /** Set when a safeguard self-halted the sleeve: 'max_drawdown' | 'daily_loss'. */
  autoPauseReason?: string | null;
  autoPausedAt?: string | null;
  lastRiskCheckAt?: string | null;
  dayStartEquity: number | null;
  dayStartDate: string | null;
  lastHeartbeatAt: string | null;
  lastError: string | null;
  lastSkipReason: string | null;
  startedAt: string | null;
  stoppedAt: string | null;
};

export type StrategyLiveState = {
  isLive: boolean;
  label: string;
  reasons: string[];
};

export type DeployStrategyOrder = {
  id: number;
  symbol: string;
  side: string;
  notional: number | null;
  qty: number | null;
  orderType: string | null;
  limitPrice: number | null;
  status: string;
  skipReason: string | null;
  clientOrderId: string;
  brokerOrderId: string | null;
  dipTier: string | null;
  signalPrice: number | null;
  targetWeightPct: number | null;
  filledAvgPrice: number | null;
  filledQty: number | null;
  createdAt: string;
};

export type DeployStrategyDashboard = {
  strategySpec: StrategySpec;
  deployment: StrategyDeployment;
  live: StrategyLiveState;
  marketOpen: boolean;
  brokerage: BrokerageConnection | null;
  account: (AlpacaAccountSummary & { equity?: number | null; buyingPower?: number | null }) | null;
  accountError: string | null;
  deployList: {
    items: Array<{
      id: number;
      userAlertId: number;
      symbol: string;
      targetWeightPct: number | null;
      weightPinned?: boolean;
      suggestedLimitMin: number | null;
      suggestedLimitMax: number | null;
      source: string;
      grokRationale: string | null;
      baselinePrice: number | null;
      actualWeightPct?: number | null;
      /** null = not checked; false = Alpaca cannot trade for this account */
      brokerTradable?: boolean | null;
      brokerTradeNote?: string | null;
      brokerExchange?: string | null;
      position: {
        symbol: string;
        qty: number | null;
        marketValue: number | null;
        avgEntryPrice: number | null;
        currentPrice: number | null;
        unrealizedPl?: number | null;
        unrealizedPlpc?: number | null;
      } | null;
    }>;
    totalTargetWeightPct: number;
    count: number;
    untradableCount?: number;
  };
  positions: Array<{
    symbol: string;
    qty: number | null;
    marketValue: number | null;
    avgEntryPrice: number | null;
    currentPrice: number | null;
    unrealizedPl?: number | null;
    unrealizedPlpc?: number | null;
  }>;
  portfolioHistory: {
    mode: string;
    period: string;
    timeframe: string;
    baseValue: number | null;
    points: Array<{ t: number; equity: number | null; profitLoss: number | null; profitLossPct: number | null }>;
  } | null;
  recentOrders: DeployStrategyOrder[];
  confirmPhrase: string;
};

export async function fetchDeployStrategyDashboard(): Promise<DeployStrategyDashboard> {
  const { data } = await axios.get('/deploy-strategy/dashboard');
  return data;
}

export async function setDeployStrategyRun(input: {
  on: boolean;
  confirmPhrase?: string;
}): Promise<{ deployment: StrategyDeployment; strategySpec: StrategySpec }> {
  const { data } = await axios.post('/deploy-strategy/run', input);
  return data;
}

export async function setDeployStrategyKillSwitch(input: {
  armed: boolean;
  confirmPhrase?: string;
}): Promise<{ deployment: StrategyDeployment; strategySpec: StrategySpec }> {
  const { data } = await axios.post('/deploy-strategy/kill-switch', input);
  return data;
}

export async function updateDeployStrategyRisk(input: {
  maxPositionPct?: number;
  maxNotionalPerOrder?: number;
  maxOpenPositions?: number;
  dailyLossHaltPct?: number;
  maxDrawdownPct?: number;
  maxDailyDeployPct?: number;
}): Promise<{ deployment: StrategyDeployment; strategySpec: StrategySpec }> {
  const { data } = await axios.patch('/deploy-strategy/risk', input);
  return data;
}

export async function recomputeIdealDeployMath(input?: {
  applyBaselines?: boolean;
}): Promise<{
  updated: number;
  applyBaselines: boolean;
  maxPositionPct: number;
  items: Array<{
    symbol: string;
    previousWeightPct: number | null;
    targetWeightPct: number;
    atrPct: number | null;
    currentBaseline: number | null;
    suggestedBaseline: number | null;
    baselineApplied: boolean;
  }>;
  pinnedSkipped?: number;
}> {
  const { data } = await axios.post('/deploy-strategy/recompute-ideal', {
    applyBaselines: input?.applyBaselines === true
  });
  return data;
}
